import { useMemo } from 'react';
import { useDrop } from 'react-dnd';

import dropIcon from '../../assets/drop-icon.svg';
import SelectableStaticList from '../builder/SelectableStaticList';
import DeleteCheckbox from '../inputs/DeleteCheckbox';
import {
  ADD_VARIABLE,
  REMOVE_CATEGORY,
  REMOVE_VARIABLE,
  TYPES,
} from '../../reducers/packageBuilderReducer';
import appIcon from '../../assets/atad-logo-bars.png';
import VariableCategory from '../builder/VariableCategory';
import YesNo from '../builder/components/YesNo';
import dataDictionary from '../../assets/data-dictionary/dataDictionary.json';
import { traverse } from '../../helpers/dataDictionary';

// A variable "needs dedup" when a single key maps to more than one dictionary
// attribute (e.g. boolean groups like AIQ_MARITAL_V2 -> Married Now /
// Single-Divorced / Single-Other). For those, the trailing id segment is an
// arbitrary value, so we label the dragged var with its last category instead.
const keyOccurrenceCount = (() => {
  const counts = new Map<string, number>();
  for (const [key, value] of traverse(dataDictionary)) {
    if (key === 'attributes' && Array.isArray(value)) {
      value.forEach((attr: any) => {
        const k = attr?.attrs?.key;
        if (k) counts.set(k, (counts.get(k) || 0) + 1);
      });
    }
  }
  return counts;
})();

function needsDedup(key?: string) {
  return !!key && (keyOccurrenceCount.get(key) || 0) > 1;
}

function DropIconCircle({ className }) {
  return (
    <div
      className={`bg-background-dark w-16 h-16 rounded-full p-3.5 ${
        className ? className : ''
      }`}
    >
      <img className="w-full h-full" src={dropIcon} alt="drop here icon" />
    </div>
  );
}

function VariablesList({ dictionary, dispatch }) {
  function subCategoriesOrAttributes(value) {
    let nodes = [];

    Object.entries(value).forEach(([key, value], idx) => {
      if (key === 'attributes') {
        if (value.length > 0) {
          nodes.push(
            <>
              {value.map((attribute) => (
                <div
                  className="flex flex-row items-center py-1 w-full"
                  key={attribute.id}
                >
                  <DeleteCheckbox
                    className="mr-2"
                    onClick={() =>
                      dispatch({
                        type: REMOVE_VARIABLE,
                        payload: {
                          id: attribute.id,
                        },
                      })
                    }
                  />
                  <YesNo id={attribute.id} name={attribute.name} />
                </div>
              ))}
            </>
          );
        }
        return;
      }

      nodes.push(
        <li className="w-full" key={idx}>
          <SelectableStaticList
            label={key}
            items={subCategoriesOrAttributes(value)}
            initialOpen={true}
          />
        </li>
      );
    });

    return nodes;
  }

  return (
    <ul>
      {Object.entries(dictionary).map(([key, value], idx) => (
        <li className="w-full" key={idx}>
          <SelectableStaticList
            label={key}
            items={subCategoriesOrAttributes(value)}
            initialOpen={true}
          />
        </li>
      ))}
    </ul>
  );
}

export function canAddVariable(variables, id) {
  let canAdd = !variables.some(({ id: variableId }) => variableId === id);
  return canAdd;
}

type EnhancementsVariablesDropzoneProps = {
  className?: string;
  dropzoneMessage: string;
  state: any;
  dispatch: any;
};

export default function EnhancementsVariablesDropzone({
  className,
  dropzoneMessage,
  state,
  dispatch,
}: EnhancementsVariablesDropzoneProps) {
  const { variablesIncluded } = state;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [TYPES.VARIABLE],
      drop: (item, monitor) => {
        const type = monitor.getItemType();

        if (type === TYPES.VARIABLE) {
          dispatch({
            type: ADD_VARIABLE,
            payload: {
              id: item.id,
              key: item.key,
            },
          });
        }
      },
      canDrop: (item, monitor) => {
        const type = monitor.getItemType();

        if (type === TYPES.VARIABLE) {
          return canAddVariable(variablesIncluded, item.id);
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        canDrop: !!monitor.canDrop(),
        itemId: monitor.getItem()?.id,
      }),
    }),
    [state]
  );

  const map = useMemo(() => {
    let map = {};

    variablesIncluded?.forEach(({ id, key }) => {
      const idPath = id.split('___');

      // For deduped vars, drop the trailing value segment so the leaf shows the
      // last category (e.g. "Marital Status" instead of "Married Now"). Guarded
      // so we never collapse below a category + leaf.
      const displayPath =
        needsDedup(key) && idPath.length > 2 ? idPath.slice(0, -1) : idPath;

      let curSelector;
      displayPath.forEach((selector, idx) => {
        if (idx === displayPath.length - 1) {
          curSelector.attributes.push({
            name: selector,
            id,
            state,
          });
          return;
        }

        if (!curSelector) {
          if (!map[selector]) {
            map[selector] = {
              attributes: [],
            };
          }
          curSelector = map[selector];
          return;
        }

        if (!curSelector[selector]) {
          curSelector[selector] = {
            attributes: [],
          };
        }

        curSelector = curSelector[selector];
      });
    });

    return map;
  }, [variablesIncluded]);

  const hasVariablesIncluded = useMemo(
    () => variablesIncluded?.length > 0,
    [variablesIncluded?.length]
  );

  return (
    <div
      className={`flex flex-row flex-grow bg-gray-100 rounded-half ${
        hasVariablesIncluded ? 'p-4 space-x-6' : ''
      }`}
      ref={drop}
    >
      <div
        className={`flex ${
          hasVariablesIncluded ? 'w-2/5 flex-col' : 'flex-grow'
        }`}
      >
        {hasVariablesIncluded && (
          <div className="flex flex-col flex-grow space-y-2 mb-3">
            {Object.entries(map).map(([key, value], idx) => (
              <VariableCategory
                key={idx}
                name={key}
                logo={<img src={appIcon} alt="logo" className="w-5 h-5" />}
                onDelete={() =>
                  dispatch({
                    type: REMOVE_CATEGORY,
                    payload: {
                      key,
                    },
                  })
                }
              />
            ))}
          </div>
        )}
        <div
          className={`flex flex-col justify-center items-center rounded-half p-3 py-5 border-2 border-dashed ${
            hasVariablesIncluded ? '' : 'flex-grow'
          } ${className ? className : ''} ${
            isOver ? 'cursor-move bg-gray-200' : ''
          }`}
        >
          <div className="flex flex-col items-center">
            <DropIconCircle
              className={`${
                isOver ? (canDrop ? 'bg-primary' : 'bg-secondary') : ''
              }`}
            />
            <p className="pt-6 text-gray-500 text-center">
              {isOver && !canDrop
                ? 'Attribute has already been pulled over.'
                : dropzoneMessage}
            </p>
          </div>
        </div>
      </div>
      {hasVariablesIncluded && (
        <div className="flex flex-col flex-grow bg-white rounded-half p-3 px-6 w-3/5 overflow-auto">
          {variablesIncluded.length > 0 && (
            <div className="">
              {<VariablesList dictionary={map} dispatch={dispatch} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
